import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, signal } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { BaseService } from '../../core/services/base.service';
import { CartItemModel } from '../models/cart-item.model';

type CartCountMsg = { tabId: string; count: number };
const CART_CHANNEL = 'cart-count';

@Injectable({ providedIn: 'root' })
export class CartItemService extends BaseService implements OnDestroy {
  override dbName = 'Cart item';
  override collection = '/cart-item';

  readonly count = signal(0);

  private readonly tabId = crypto.randomUUID();
  private readonly channel = new BroadcastChannel(CART_CHANNEL);

  constructor(http: HttpClient) {
    super(http, 'Cart item', '/cart-item', CartItemModel);
    this.channel.addEventListener('message', (evt: MessageEvent<CartCountMsg>) => {
      if (evt.data.tabId !== this.tabId) {
        this.count.set(evt.data.count);
      }
    });
  }

  ngOnDestroy(): void {
    this.channel.close();
  }

  private broadcast(): void {
    this.channel.postMessage({ tabId: this.tabId, count: this.count() });
  }

  // Counts
  countItemsInCart(): Observable<number> {
    return this.http
      .get<{ data: number }>(`${this.uri}/count`, {
        headers: this.defaultHeaders,
      })
      .pipe(
        map((response) => response.data),
        tap((count) => {
          this.count.set(count);
          this.broadcast();
        })
      );
  }

  incrementCount(by = 1): void {
    this.count.update((n) => n + by);
    this.broadcast();
  }

  decrementCount(by = 1): void {
    this.count.update((n) => Math.max(0, n - by));
    this.broadcast();
  }

  resetCount(): void {
    this.count.set(0);
    this.broadcast();
  }

  // Functions
  addToCart(articleId: number): Observable<CartItemModel | null> {
    return this.http
      .post<{ data?: unknown }>(
        `${this.uri}`,
        { isSample: true, articleId },
        {
          headers: this.defaultHeaders,
        }
      )
      .pipe(
        map((response: any) => (response?.data ? new this.Model(response.data) : null)),
        tap(() => this.incrementCount())
      );
  }

  addBulkToCart(articleIds: number[]): Observable<CartItemModel[] | null> {
    return this.http
      .post<{ data?: unknown }>(
        `${this.uri}/bulk`,
        { items: articleIds.map((id) => ({ isSample: true, articleId: id })) },
        {
          headers: this.defaultHeaders,
        }
      )
      .pipe(
        map((response: any) =>
          response?.data ? response.data.map((row: any) => new this.Model(row)) : []
        ),
        tap((data: CartItemModel[]) => this.incrementCount(data.length))
      );
  }

  removeInsufficientStockItems<T>(items: any[], options = {}): Observable<T> {
    return this.http.post<T>(
      `${this.uri}/remove-insufficient-stock`,
      { items },
      {
        headers: this.defaultHeaders,
        ...options,
      }
    );
  }
}
